import React, { useCallback } from "react";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { AHIcon, AHSeparator, AHText, Box, ListItem } from "arkh";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useRefetchableFragment,
} from "react-relay";
import { OrgSpaceActiveInfoScreenQuery } from "./__generated__/OrgSpaceActiveInfoScreenQuery.graphql";
import { OrgSpaceActiveInfoScreen_workspace$key } from "./__generated__/OrgSpaceActiveInfoScreen_workspace.graphql";
import { FlatList } from "react-native";
import { OrgSpaceActiveInfoScreenListItem_SpaceActiveConnectionNode$key } from "./__generated__/OrgSpaceActiveInfoScreenListItem_SpaceActiveConnectionNode.graphql";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../common/navigationHeaderOptions";
import { MoreButton } from "../../../common/MoreButton";
import { useSetCurrentStoreId } from "../../../OS/Login/State";
import { FormattedMessage } from "react-intl";

type ParamList = {
  OrgSpaceActiveInfoMain: {
    spaceId: string;
    workspaceId: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgSpaceActiveInfoScreen = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"OrgSpaceActiveInfo">) => {
    const getNavigationOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="OrgSpaceActiveInfoMain"
          component={OrgSpaceActiveInfoComponent}
          initialParams={props.route.params}
          options={({ navigation }) =>
            getNavigationOptions({
              type: "CLOSE",
              onClose: () => navigation.goBack(),
              title: "",
            })
          }
        />
      </Stack.Navigator>
    );
  },
  true
);

export type OrgUserInviteScreenProps = {
  route: RouteProp<ParamList, "OrgSpaceActiveInfoMain">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgSpaceActiveInfo">,
    NativeStackNavigationProp<ParamList, "OrgSpaceActiveInfoMain">
  >;
};

function OrgSpaceActiveInfoComponent(props: OrgUserInviteScreenProps) {
  const data = useLazyLoadQuery<OrgSpaceActiveInfoScreenQuery>(
    graphql`
      query OrgSpaceActiveInfoScreenQuery(
        $spaceId: ID!
        $workspaceId: ID!
        $first: Int
        $after: String
      ) {
        workspace(id: $workspaceId) {
          space(id: $spaceId) {
            name
          }
          ...OrgSpaceActiveInfoScreen_workspace
        }
      }
    `,
    {
      spaceId: props.route.params.spaceId,
      workspaceId: props.route.params.workspaceId,
    }
  );

  const name = data.workspace.space?.name ?? "";

  useNavigationHeader({
    type: "CLOSE",
    onClose: () => props.navigation.goBack(),
    title: name,
  });

  const setCurrentStoreId = useSetCurrentStoreId();

  const onSelectDashboard = useCallback(
    (storeId: string, dashboardId: string, timezone: string) => {
      props.navigation.pop();
      setCurrentStoreId(storeId, timezone);
      props.navigation.navigateDeprecated("Dashboard", {
        dashboardId,
      });
    },
    [props.navigation, setCurrentStoreId]
  );

  return (
    <OrgSpaceActiveInfoList
      workspace={data.workspace}
      onSelectDashboard={onSelectDashboard}
    />
  );
}

function OrgSpaceActiveInfoList(props: {
  workspace: OrgSpaceActiveInfoScreen_workspace$key;
  onSelectDashboard: (
    storeId: string,
    dashboardId: string,
    timezone: string
  ) => void;
}) {
  const [data] = useRefetchableFragment(
    graphql`
      fragment OrgSpaceActiveInfoScreen_workspace on Workspace
      @refetchable(
        queryName: "OrgSpaceActiveInfoScreen_workspacePaginationQuery"
      ) {
        id
        space(id: $spaceId) {
          stores(first: $first, after: $after)
            @connection(
              key: "OrgSpaceActiveInfoScreen_workspace_active_stores"
            ) {
            edges {
              node {
                ...OrgSpaceActiveInfoScreenListItem_SpaceActiveConnectionNode
              }
            }
          }
        }
      }
    `,
    props.workspace
  );

  const listData = data.space.stores.edges;

  return (
    <Box
      color="background"
      style={{ flex: 1 }}
      spaceHorizontal="medium"
      spaceVertical="medium"
    >
      <ListItem isFirst>
        <RowWrapper
          icon={null}
          title={
            <AHText numberOfLines={1} style={{ flex: 1 }} type="subtitle">
              <FormattedMessage
                description="OrgSpaceActiveInfoList label store"
                defaultMessage="Store"
              />
            </AHText>
          }
          detail={
            <AHText type="subtitle" numberOfLines={1}>
              <FormattedMessage
                description="OrgSpaceActiveInfoList label dashboards with space"
                defaultMessage="Dashboards with Space"
              />
            </AHText>
          }
        />
      </ListItem>
      <AHSeparator />
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        data={listData}
        renderItem={({ item, index }) => {
          return (
            <OrgSpaceStoreListItem
              node={item.node}
              isLast={index + 1 === listData.length}
              onPress={props.onSelectDashboard}
            />
          );
        }}
      />
    </Box>
  );
}

function OrgSpaceStoreListItem(props: {
  node: OrgSpaceActiveInfoScreenListItem_SpaceActiveConnectionNode$key;
  onPress: (storeId: string, dashboardId: string, timezone: string) => void;
  isLast?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment OrgSpaceActiveInfoScreenListItem_SpaceActiveConnectionNode on SpaceActiveConnectionNode {
        store {
          id
          name
          timezone
        }
        activeStorecastWithSpaceCount

        activeDashboards {
          id
          name
        }
      }
    `,
    props.node
  );

  return (
    <MoreButton
      items={data.activeDashboards.map((d) => ({
        label: d.name,
        onPress: () => {
          if (data.store?.id) {
            props.onPress(data.store.id, d.id, data.store.timezone);
          }
        },
      }))}
    >
      {(op) => (
        <ListItem isLast={props.isLast} onPress={op}>
          <RowWrapper
            icon={<AHIcon type="store" color="text-washed" />}
            title={
              <AHText numberOfLines={1} style={{ flex: 1 }} type="subtitle">
                {data.store?.name ?? ""}
              </AHText>
            }
            detail={
              <AHText type="subtitle">
                {data.activeStorecastWithSpaceCount}
              </AHText>
            }
          />
        </ListItem>
      )}
    </MoreButton>
  );
}

export function RowWrapper(props: {
  icon: React.ReactNode | null;
  title: React.ReactNode;

  detail: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Box style={{ width: "70%", flexDirection: "row", alignItems: "center" }}>
        {props.icon ? <Box spaceRight="small">{props.icon}</Box> : null}
        <Box style={{ flex: 1 }} spaceRight="small">
          {props.title}
        </Box>
      </Box>

      <Box style={{ width: "30%" }}>{props.detail}</Box>
      {props.action ? (
        <Box
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            justifyContent: "center",
          }}
        >
          {props.action}
        </Box>
      ) : null}
    </Box>
  );
}
